import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_TRIAL_SHARING } from "redux/features/trialCalendar/trialCalendarGql";
import { Button } from "../button";
import { TextInput } from "../textInput";
import { globalVariables } from "helper/globalVariables";
import { useSelector } from "react-redux";

export const ShareTrialAccess = ({ trialCalendarId, isOwner, initialSharedUsers = [] }) => {
  const [email, setEmail] = useState("");
  const [sharedUsers, setSharedUsers] = useState(initialSharedUsers);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);

  const [updateTrialSharing] = useMutation(UPDATE_TRIAL_SHARING);

  // Get user role from Redux state
  const { auth } = useSelector((state) => state);
  const userRole = auth?.authReducer?.role;

  // Update shared users when initialSharedUsers changes
  useEffect(() => {
    setSharedUsers(initialSharedUsers);
  }, [initialSharedUsers]);

  // Debug: Show what we're getting
  console.log("ShareTrialAccess - trialCalendarId:", trialCalendarId, "isOwner:", isOwner, "userRole:", userRole);

  // Only show for Host and Super Admin roles, AND they must be the owner
  // Members should NEVER see this, even if they somehow own the trial
  const canManageSharing = isOwner && (userRole === "Host" || userRole === "Super Admin");

  if (!canManageSharing) {
    return null;
  }

  const handleAddUser = async () => {
    if (!email || !email.includes("@")) {
      setMessage({ text: "Please enter a valid email address", type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const { data } = await updateTrialSharing({
        variables: {
          trialCalendarId: parseInt(trialCalendarId),
          helperEmail: email.toLowerCase(),
          action: "add",
        },
      });

      setSharedUsers([...sharedUsers, data.updateTrialSharing.user]);
      setMessage({
        text: data.updateTrialSharing.message,
        type: "success",
      });
      setEmail("");
    } catch (error) {
      setMessage({
        text: error.message || "Failed to add team member",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userEmail, userAuthId) => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const { data } = await updateTrialSharing({
        variables: {
          trialCalendarId: parseInt(trialCalendarId),
          helperEmail: userEmail,
          action: "remove",
        },
      });

      setSharedUsers(sharedUsers.filter((u) => u.authId !== userAuthId));
      setMessage({
        text: data.updateTrialSharing.message,
        type: "success",
      });
    } catch (error) {
      setMessage({
        text: error.message || "Failed to remove team member",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 pt-8 border-t border-gray-300">
      <h3 className="text-gray-900 font-sans text-xl font-semibold leading-7 mb-4">
        Share Trial Access
      </h3>
      <p className="text-gray-700 text-base mb-3">
        Add team members who can view this trial, download reports, and edit SIT statuses.
      </p>
      <p className="text-gray-700 text-base mb-4 font-bold">
        Note: Team members are added by their email address.
      </p>

      <div className="flex gap-2 mb-4">
        <div className="flex-1">
          <TextInput
            type="email"
            placeholder="Enter team member's email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            textTransform="none"
          />
        </div>
        <Button
          label="Add Team Member"
          onClick={handleAddUser}
          onlyDisabled={!email}
          width="w-auto px-4"
        />
      </div>

      {message.text && (
        <div
          className={`p-3 rounded mb-4 ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {sharedUsers.length > 0 && (
        <div className="mt-4">
          <h4 className="text-gray-700 font-semibold mb-2">Team Members:</h4>
          <div className="space-y-2">
            {sharedUsers.map((user) => (
              <div
                key={user.authId}
                className="flex justify-between items-center p-3 bg-gray-50 rounded"
              >
                <div>
                  <p className="font-medium text-gray-900">{user.name}</p>
                  <p className="text-sm text-gray-600">{user.email}</p>
                </div>
                <button
                  onClick={() => handleRemoveUser(user.email, user.authId)}
                  disabled={loading}
                  className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

